import React from "react";
import { Modal } from "@shopify/polaris";
import styles from "./DeleteModal.module.css";
import MainFrameContextType from "../../types/MainFrameContextType";
import { MainFrameContext } from "../contexts/MainFrameContext";
import ElementDocument from "../../types/ElementDocument";
import { ClipLoader } from "react-spinners";
import { useDispatch, useSelector } from "react-redux";
import { deleteAssetAction } from "../../store/actions/Asset";
import { AssetType } from "../../types/AssetType";
import { PortalState } from "../../types/PortalState";
import ElementAsset from "../../types/ElementAsset";
import { useTranslation } from "react-i18next";
import { SystemMessage } from "../../config/SystemMessage";
import ReactGA from "react-ga4";

interface Props {
    elementId: string;
    asset: ElementDocument | ElementAsset;
    active: boolean;
    closeModal: () => void;
}

export default function DeleteModal({
    elementId,
    asset,
    active,
    closeModal
}: Props) {
    const { createToast } = React.useContext(
        MainFrameContext
    ) as MainFrameContextType;
    const [error, setError] = React.useState<null | string>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const currentInspection = useSelector(
        (state: PortalState) => state.portal.activeInspection
    );

    const currentElement = useSelector((state: any) =>
        state.portal.activeInspection.sections.find(
            (section: any) => section.Id === elementId
        )
    );

    const { t } = useTranslation("common");
    const dispatch = useDispatch();

    const getDeleteMessage = (assetType: AssetType) => {
        switch (assetType) {
            case AssetType.CheckList:
                return t(SystemMessage.CHECKLIST_DELETED);

            case AssetType.Document:
                return t(SystemMessage.DOCUMENT_DELETED);

            case AssetType.Image:
                return t(SystemMessage.IMAGE_DELETED);

            default:
                return t(SystemMessage.IMAGE_DELETED);
        }
    };

    async function deleteElement() {
        setLoading(true);
        try {
            const deleteMessage = getDeleteMessage(asset.type);

            if (currentInspection?.Id) {
                await dispatch(
                    deleteAssetAction(
                        currentInspection.Id,
                        elementId,
                        asset.id,
                        asset.type,
                        currentElement.ReUseDocuments,
                    )
                );
                createToast("success", deleteMessage);
                ReactGA.event({
                    category: "Delete element",
                    action: "success",
                    label: asset.id,
                    value: 0
                });
                setLoading(false);
                closeModal();
            }
        } catch (e) {
            console.error(e);
            setError("Failed to delete asset, please try again.");
            ReactGA.event({
                category: "Delete element",
                action: "failed",
                label: asset.id,
                value: 0
            });
            setLoading(false);
        }
    }

    React.useEffect(() => {
        setError(null);
    }, [active]);

    return (
        <Modal open={active} onClose={closeModal} title="Delete Asset">
            <Modal.Section>
                <div>
                    <div className={styles.modalIcon}>
                        <img
                            width="105px"
                            height="129px"
                            src="/images/Large-Trash.svg"
                            alt="Bin"
                        />
                    </div>
                    <div>
                        <p className={styles.modalText}>
                            Are you sure you want to delete {asset.name}
                        </p>
                    </div>
                    <div className={styles.modalButtons}>
                        <div
                            onClick={() => {
                                closeModal();
                            }}
                            className={styles.cancelButton}
                        >
                            Cancel
                        </div>
                        <div
                            onClick={deleteElement}
                            className={styles.deleteButton}
                        >
                            {loading ? (
                                <div className={styles.modalLoader}>
                                    <ClipLoader
                                        color={"#FFFFFF"}
                                        loading={true}
                                        size={"30px"}
                                    />
                                </div>
                            ) : (
                                <div>Delete</div>
                            )}
                        </div>
                    </div>
                    {error ? <div className={styles.error}>{error}</div> : null}
                </div>
            </Modal.Section>
        </Modal>
    );
}
